import { useTranslation } from "react-i18next"

import type { FairUseTier } from "@/api/admin/fair-use"

import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { useConfigQuery } from "@/hooks/use-config-query"

import { MODEL_PLAN_IDS, toggleModelPlan } from "./model-plans-utils"

type ModelPlansFieldProps = {
  /** `null` = every plan, which is the unrestricted state the backend stores. */
  plans: FairUseTier[] | null
  disabled?: boolean
  onChange: (plans: FairUseTier[] | null) => void
}

const PLAN_LABEL_KEYS: Record<FairUseTier, { key: string; fallback: string }> = {
  standard: { key: "adminDashboard.fairUse.plans.standard", fallback: "Standard" },
  pro: { key: "adminDashboard.fairUse.plans.pro", fallback: "Pro" },
  unlimited: { key: "adminDashboard.fairUse.plans.unlimited", fallback: "Unlimited" },
}

/**
 * Which fair-use plans may choose this model (#3087, spec R2).
 *
 * Three checkboxes rather than a multi-select, because the whole vocabulary is
 * three words and a closed control would hide the answer to the only question
 * this field is asked: "is this model restricted, and to whom".
 *
 * All three ticked IS the unrestricted state — `toggleModelPlan` collapses it
 * to `null`, which is what the backend stores (it removes the key rather than
 * writing a full list), so the dialog cannot produce a "restriction" that
 * restricts nothing. The same collapse catches the last box being unticked: a
 * model available to no plan is not a state the platform has.
 *
 * The restriction is enforced server-side and the model is also filtered out of
 * `/api/config` for other plans, so this is a commercial control, not the
 * access policy — group-based access lives on its own dialog.
 *
 * **Who may write it** is the same answer as every other fair-use entitlement:
 * the platform. `PUT /api/admin/models/<id>/plans` answers 403
 * `platform_admin_required` to a delegated admin, so they get the allowlist as
 * values instead of as disabled boxes — the same read-only shape the
 * entitlement matrix uses, and read from the same `is_platform_admin` flag.
 * The flag is read here rather than passed in so the rule travels with the
 * control; a read-only render also never moves `plans`, so the dialog's save
 * finds nothing to write.
 */
export function ModelPlansField({ plans, disabled, onChange }: ModelPlansFieldProps) {
  const { t } = useTranslation()
  const { data: appConfig } = useConfigQuery()
  const canEdit = appConfig?.user_policy.is_platform_admin ?? false
  const restricted = plans !== null && plans.length > 0
  const includes = (plan: FairUseTier) => !restricted || plans.includes(plan)

  return (
    <div className="space-y-2 rounded-md border p-3" data-testid="model-plans-field">
      <div className="space-y-0.5">
        <Label>
          {t("adminDashboard.modelEdit.plansLabel", { defaultValue: "Available to plans" })}
        </Label>
        <p className="text-muted-foreground text-xs">
          {canEdit
            ? t("adminDashboard.modelEdit.plansHelp", {
                defaultValue:
                  "Which fair-use plans may pick this model. Tick all three — or none — to leave it open to everyone. Only applies while fair use is on.",
              })
            : t("adminDashboard.modelEdit.plansReadOnlyHelp", {
                defaultValue:
                  "Which fair-use plans may pick this model. Set by your platform provider, and applies only while fair use is on.",
              })}
        </p>
      </div>
      <div className="flex flex-wrap gap-4">
        {MODEL_PLAN_IDS.map((plan) => {
          const id = `model-plan-${plan}`
          const label = t(PLAN_LABEL_KEYS[plan].key, {
            defaultValue: PLAN_LABEL_KEYS[plan].fallback,
          })
          if (!canEdit) {
            return (
              <Badge key={plan} data-testid={id} variant={includes(plan) ? "secondary" : "outline"}>
                {label}
              </Badge>
            )
          }
          return (
            <div key={plan} className="flex items-center gap-2">
              <Checkbox
                checked={includes(plan)}
                disabled={disabled}
                id={id}
                onCheckedChange={(checked) =>
                  onChange(toggleModelPlan(plans, plan, checked === true))
                }
              />
              <Label className="text-sm font-normal" htmlFor={id}>
                {label}
              </Label>
            </div>
          )
        })}
      </div>
      <p className="text-muted-foreground text-xs">
        {restricted
          ? t("adminDashboard.modelEdit.plansRestricted", {
              defaultValue: "Restricted — users on other plans will not see this model.",
            })
          : t("adminDashboard.modelEdit.plansEveryone", {
              defaultValue: "Open to every plan.",
            })}
      </p>
    </div>
  )
}
